import React, { useState, useEffect } from "react";
import OnboardingHeader from "./OnboardingHeader";
import StepProgressBar from "./StepProgressBar";
import OnboardingButton from "./OnboardingButton";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { updateStage } from "../../store/reducers/stageReducer";
import { useUpdateStagesMutation } from "../../services/api";
import { showNotification } from "../../services/showNotification";
import Tooltip from "../Tooltip";

// This screen's step in the onboarding flow (see ONBOARDING_STEPS).
const STEP_KEY = "scheduleACallCompleted" as const;

const ScheduleCall = () => {
  const dispatch = useAppDispatch();
  const zohoCustomerId = useAppSelector((state) => state.login.zohoCustomerId);
  const userEmail = useAppSelector((state) => state.purlUser.email);
  const firstName = useAppSelector((state) => state.purlUser.firstName);
  const lastName = useAppSelector((state) => state.purlUser.lastName);
  const userName = [firstName, lastName].filter(Boolean).join(" ");
  const isAlreadyCompleted = useAppSelector((state) => state.stage?.scheduleACallCompleted);
  const [isCompleted, setIsCompleted] = useState<boolean>(isAlreadyCompleted || false);
  const [submitType, setSubmitType] = useState<"continue" | "skip" | null>(null);
  const [updateStages, { isLoading: isUpdating }] = useUpdateStagesMutation();
  const calendlyBaseUrl = process.env.REACT_APP_CALENDLY_SCHEDULE_A_CALL_URL || "";
  console.log("calendlyBaseUrl::::",calendlyBaseUrl);
  let calendlyEmbedUrl = `${calendlyBaseUrl}?embed_domain=1&embed_type=Inline`;
  if (userEmail) calendlyEmbedUrl += `&email=${encodeURIComponent(userEmail)}`;
  if (userName) calendlyEmbedUrl += `&name=${encodeURIComponent(userName)}`;

  useEffect(() => {
    const handleCalendlyEvent = async (e: MessageEvent) => {
      let eventData = e.data;
      if (typeof eventData === "string") {
        try {
          eventData = JSON.parse(eventData);
        } catch (error) {
          return;
        }
      }

      if (eventData?.event === "calendly.event_scheduled") {
        setIsCompleted(true);
        try {
          await updateStages({ zohoId: zohoCustomerId, key: STEP_KEY }).unwrap();
        } catch (error) {
          console.error("Auto-save failed:", error);
        }
      }
    };
    window.addEventListener("message", handleCalendlyEvent);
    return () => window.removeEventListener("message", handleCalendlyEvent);
  }, [updateStages, zohoCustomerId]);

  const handleProceed = async (type: "continue" | "skip") => {
    setSubmitType(type);
    try {
      if (type === "skip") {
        await updateStages({ zohoId: zohoCustomerId, key: STEP_KEY }).unwrap();
      }
      dispatch(updateStage({ [STEP_KEY]: true }));
    } catch {
      showNotification("error", "Something went wrong. Please try again.");
      setSubmitType(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <OnboardingHeader />

      <main className="w-full max-w-5xl mx-auto p-10">
        <StepProgressBar stepKey={STEP_KEY} />

        <div className="mt-6 flex flex-col items-center">
          <h1 className="font-['Canela_Text',_serif] font-bold text-3xl uppercase text-[#000000] text-center">
            Schedule Your Private Consultation
          </h1>

          <div className="mt-6 w-full">
            <div className="w-full lg:w-[1050px] h-[700px] bg-[#FFFFFF] border-0 rounded-lg overflow-hidden">
              <iframe
                src={calendlyEmbedUrl}
                width="100%"
                height="100%"
                frameBorder="0"
                title="Schedule Consultation"
                className="w-full h-full bg-[#F8F9FA]"
              ></iframe>
            </div>
          </div>

          <div className="mt-6 w-full max-w-[500px] flex flex-row items-center justify-center gap-4">
            <Tooltip
              text="Please schedule a call to continue, or select 'Do This Later' to skip this step."
              show={!isCompleted}
              className="flex-1 w-full"
            >
              <OnboardingButton
                title="CONTINUE"
                variant="filled"
                onClick={() => handleProceed("continue")}
                loading={isUpdating && submitType === "continue"}
                disabled={!isCompleted || isUpdating}
              />
            </Tooltip>

            {!isCompleted && (
              <div className="flex-1 w-full">
                <OnboardingButton
                  title="DO THIS LATER"
                  variant="secondary"
                  onClick={() => handleProceed("skip")}
                  loading={isUpdating && submitType === "skip"}
                  disabled={isUpdating}
                />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ScheduleCall;